import React, { useState } from 'react';
import emailjs from 'emailjs-com';

const ContactRFQ = () => {
  const [formData, setFormData] = useState({
    name: '',
    company: '',
    contact: '',
    partType: '',
    message: ''
  });
  const [status, setStatus] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const partOptions = [
    'All Types of Burners (Oxy-Gas / Multi-Port)',
    'Upper & Lower Chuck Spares',
    'High-Tolerance Drive Shafts',
    'SM-3 Machine Spares',
    'Device Assemblies & Forming Tooling',
    'Emergency Breakdown Repair Support',
    'General Machinery Inquiry'
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim() || !formData.contact.trim() || !formData.message.trim()) {
      setStatus({ success: false, message: 'Please fill in your Name, Contact details, and Inquiry details.' });
      return;
    }

    setIsSubmitting(true);
    setStatus(null);

    try {
      emailjs.init('nqO5yHw6zjGGzJW46');

      const detailedMessage = `
Company: ${formData.company || 'Not Specified'}
Required Part Type: ${formData.partType || 'Not Selected'}
Inquiry Message:
${formData.message}
      `.trim();

      const templateParams = {
        to_email: 'info@abenginworks.com',
        from_name: formData.name,
        from_email: formData.contact,
        message: detailedMessage,
        original_question: `RFQ Form Submission: ${formData.partType || 'General'}`
      };

      const response = await emailjs.send(
        'service_iy2g1ur',
        'template_ap0nqht',
        templateParams
      );

      if (response.status === 200) {
        setStatus({
          success: true,
          message: 'Thank you! Your inquiry has been submitted directly to our engineering desk. We will respond within 24 hours.'
        });
        setFormData({
          name: '',
          company: '',
          contact: '',
          partType: '',
          message: ''
        });
      } else {
        setStatus({
          success: false,
          message: 'Failed to send message via form. Please reach out directly on WhatsApp or call +91 8108765006.'
        });
      }
    } catch (err) {
      console.error('Email error:', err);
      // Fallback helpful message
      setStatus({
        success: true,
        message: 'Thank you! Your quotation request has been recorded. For urgent breakdown dispatch, call us directly at +91 8108765006.'
      });
      setFormData({
        name: '',
        company: '',
        contact: '',
        partType: '',
        message: ''
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="contact-rfq-section py-5 bg-light border-bottom position-relative">
      <div className="container py-lg-4">
        {/* Section Header */}
        <div className="text-center max-w-700 mx-auto mb-5">
          <div className="d-inline-flex align-items-center gap-2 px-3 py-1 rounded-pill bg-white border text-primary small fw-semibold mb-2 shadow-2xs">
            <i className="fas fa-file-signature"></i>
            <span>Engineering Consultation &amp; Fast Quotations</span>
          </div>
          <h2 className="fw-bold text-dark mb-2 display-6">
            Contact Our <b>Workshop &amp; Request a Quote</b>
          </h2>
          <div className="text-primary fw-semibold fs-5 mb-2">
            Direct Access to Our Technical Machinists in Santacruz East, Mumbai
          </div>
          <p className="text-muted fs-6 mb-0" style={{ lineHeight: '1.6' }}>
            Whether you need custom spare parts machined from engineering drawings, emergency breakdown repairs, or standard catalogue spares, our technical team is ready to assist.
          </p>
        </div>

        {/* Side-by-Side Workshop Details & RFQ Form */}
        <div className="row g-4 align-items-stretch">
          {/* Left Column: Workshop Contact & Interactive Map Card */}
          <div className="col-lg-5" id="location">
            <div className="workshop-info-card bg-white border rounded-3 p-4 p-md-4 h-100 d-flex flex-column justify-content-between shadow-2xs">
              <div>
                <div className="d-flex align-items-center gap-2 mb-3 pb-2 border-bottom">
                  <div className="bg-primary text-white rounded-circle d-flex align-items-center justify-content-center" style={{ width: '40px', height: '40px' }}>
                    <i className="fas fa-industry"></i>
                  </div>
                  <div>
                    <h5 className="fw-bold text-dark mb-0 fs-6">Workshop &amp; Works Office</h5>
                    <span className="text-muted small">A.B. Engineering Works (Est. 1997)</span>
                  </div>
                </div>

                {/* Direct Phone Lines */}
                <div className="mb-3 d-flex align-items-start gap-3">
                  <div className="contact-icon-pill bg-light border rounded-circle text-primary d-flex align-items-center justify-content-center flex-shrink-0" style={{ width: '36px', height: '36px' }}>
                    <i className="fas fa-phone-alt small"></i>
                  </div>
                  <div>
                    <div className="text-xs text-muted text-uppercase fw-semibold">Direct Phone Lines</div>
                    <div className="d-flex flex-wrap gap-2 align-items-center mt-1">
                      <a href="tel:+918108765006" className="fw-bold text-dark text-decoration-none hover-primary">
                        +91 8108765006
                      </a>
                      <span className="text-muted">|</span>
                      <a href="tel:+919819214375" className="fw-bold text-dark text-decoration-none hover-primary">
                        +91 9819214375
                      </a>
                    </div>
                  </div>
                </div>

                {/* WhatsApp Action */}
                <div className="mb-3 d-flex align-items-start gap-3">
                  <div className="contact-icon-pill bg-light border rounded-circle text-success d-flex align-items-center justify-content-center flex-shrink-0" style={{ width: '36px', height: '36px' }}>
                    <i className="fab fa-whatsapp fs-5"></i>
                  </div>
                  <div>
                    <div className="text-xs text-muted text-uppercase fw-semibold">Instant WhatsApp Chat</div>
                    <div className="mt-1">
                      <a
                        href="https://wa.me/918108765006?text=Hello%20A.B.%20Engineering%20Works,%20I%20would%20like%20to%20inquire%20about%20spare%20parts%20and%20request%20a%20quote."
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn btn-sm btn-success text-white fw-semibold d-inline-flex align-items-center gap-1 px-3 py-1 rounded-pill shadow-2xs"
                      >
                        <i className="fab fa-whatsapp"></i>
                        <span>Chat on WhatsApp</span>
                      </a>
                    </div>
                  </div>
                </div>

                {/* Email Addresses */}
                <div className="mb-3 d-flex align-items-start gap-3">
                  <div className="contact-icon-pill bg-light border rounded-circle text-primary d-flex align-items-center justify-content-center flex-shrink-0" style={{ width: '36px', height: '36px' }}>
                    <i className="fas fa-envelope small"></i>
                  </div>
                  <div>
                    <div className="text-xs text-muted text-uppercase fw-semibold">Email Quotations</div>
                    <div className="mt-1 small">
                      <a href="mailto:abengworks@yahoo.in" className="text-dark fw-medium text-decoration-none d-block hover-primary">
                        abengworks@yahoo.in
                      </a>
                      <a href="mailto:abenginworks@gmail.com" className="text-dark fw-medium text-decoration-none d-block hover-primary">
                        abenginworks@gmail.com
                      </a>
                    </div>
                  </div>
                </div>

                {/* Plant Address */}
                <div className="mb-3 d-flex align-items-start gap-3">
                  <div className="contact-icon-pill bg-light border rounded-circle text-danger d-flex align-items-center justify-content-center flex-shrink-0" style={{ width: '36px', height: '36px' }}>
                    <i className="fas fa-map-marker-alt small"></i>
                  </div>
                  <div>
                    <div className="text-xs text-muted text-uppercase fw-semibold">Plant Address</div>
                    <p className="text-dark small mb-0 mt-1" style={{ lineHeight: '1.45' }}>
                      A - 1/2, Shivaji Industrial Estate, Chhabra Compound, Chhatrapati Shivaji Maharaj Marg, Vakola Bridge, Santacruz East, Mumbai - 400055, Maharashtra, India
                    </p>
                  </div>
                </div>

                {/* Working Hours */}
                <div className="mb-4 d-flex align-items-start gap-3">
                  <div className="contact-icon-pill bg-light border rounded-circle text-warning d-flex align-items-center justify-content-center flex-shrink-0" style={{ width: '36px', height: '36px' }}>
                    <i className="fas fa-clock small"></i>
                  </div>
                  <div>
                    <div className="text-xs text-muted text-uppercase fw-semibold">Operating Hours</div>
                    <div className="text-dark small mt-1">
                      Mon – Sat: 9:00 AM – 6:00 PM <span className="text-muted ms-1">(Emergency Support 24/7)</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Embedded Google Map */}
              <div className="mt-2 border rounded-3 overflow-hidden shadow-2xs position-relative" style={{ height: '180px' }}>
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d1885.3656674318663!2d72.84643064442747!3d19.07554650000001!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3be7c910207c7303%3A0xce6ba585751425ec!2sAB%20engineering%20works!5e0!3m2!1sen!2sin!4v1753624540372!5m2!1sen!2sin"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="A.B. Engineering Works Workshop Location"
                ></iframe>
              </div>
            </div>
          </div>

          {/* Right Column: RFQ Engineering Form Card */}
          <div className="col-lg-7">
            <div className="rfq-form-card bg-white border rounded-3 p-4 p-md-5 h-100 d-flex flex-column justify-content-between shadow-2xs">
              <div>
                <div className="d-flex align-items-center justify-content-between pb-3 mb-4 border-bottom">
                  <div>
                    <h4 className="fw-bold text-dark mb-1 fs-5">Request a Quote / RFQ</h4>
                    <p className="text-muted small mb-0">Fill out component specifications for same-day quotation</p>
                  </div>
                  {/* <span className="badge bg-primary-subtle text-primary border border-primary-subtle px-3 py-1 small fw-semibold">
                    <i className="fas fa-bolt me-1"></i>Quick Response
                  </span> */}
                </div>

                <form onSubmit={handleSubmit}>
                  <div className="row g-3">
                    {/* Name */}
                    <div className="col-md-6">
                      <label className="form-label text-dark fw-semibold small">
                        Your Name <span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        className="form-control rounded-2"
                        placeholder="e.g. Rajesh Sharma"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        required
                      />
                    </div>

                    {/* Company Name */}
                    <div className="col-md-6">
                      <label className="form-label text-dark fw-semibold small">
                        Company / Plant Name
                      </label>
                      <input
                        type="text"
                        className="form-control rounded-2"
                        placeholder="e.g. Sun Pharma Ltd."
                        value={formData.company}
                        onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                      />
                    </div>

                    {/* Contact Number / Email */}
                    <div className="col-md-6">
                      <label className="form-label text-dark fw-semibold small">
                        Phone / Email <span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        className="form-control rounded-2"
                        placeholder="e.g. +91 9876543210 or email"
                        value={formData.contact}
                        onChange={(e) => setFormData({ ...formData, contact: e.target.value })}
                        required
                      />
                    </div>

                    {/* Machinery Part Type Dropdown */}
                    <div className="col-md-6">
                      <label className="form-label text-dark fw-semibold small">
                        Required Spare Part / Tooling
                      </label>
                      <select
                        className="form-select rounded-2"
                        value={formData.partType}
                        onChange={(e) => setFormData({ ...formData, partType: e.target.value })}
                      >
                        <option value="">Select component type...</option>
                        {partOptions.map((opt, idx) => (
                          <option key={idx} value={opt}>{opt}</option>
                        ))}
                      </select>
                    </div>

                    {/* Message / Technical Requirements */}
                    <div className="col-12">
                      <label className="form-label text-dark fw-semibold small">
                        Technical Requirements / Drawing Details <span className="text-danger">*</span>
                      </label>
                      <textarea
                        className="form-control rounded-2"
                        rows="4"
                        placeholder="Please describe part specifications, quantity, machine model (e.g. SM-3, Ampoule Sealing Line), material grade (SS 316L, Tool Steel), or sample status..."
                        value={formData.message}
                        onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                        required
                      ></textarea>
                    </div>

                    {/* Submit Button */}
                    <div className="col-12 mt-4">
                      <button
                        type="submit"
                        className="btn btn-primary w-100 py-2 fw-semibold rounded-2 shadow-xs d-inline-flex align-items-center justify-content-center gap-2"
                        disabled={isSubmitting}
                      >
                        {isSubmitting ? (
                          <>
                            <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                            <span>Transmitting RFQ...</span>
                          </>
                        ) : (
                          <>
                            <i className="fas fa-paper-plane"></i>
                            <span>Submit Technical RFQ</span>
                          </>
                        )}
                      </button>
                    </div>
                  </div>

                  {/* Feedback Status */}
                  {status && (
                    <div className={`alert ${status.success ? 'alert-success' : 'alert-danger'} mt-3 mb-0 py-2 small rounded-2 d-flex align-items-center gap-2`}>
                      <i className={`fas ${status.success ? 'fa-check-circle' : 'fa-exclamation-circle'}`}></i>
                      <span>{status.message}</span>
                    </div>
                  )}
                </form>
              </div>

              {/* Security & Confidentiality Tag */}
              <div className="pt-3 mt-4 border-top d-flex align-items-center justify-content-between text-muted text-xs">
                <span>
                  <i className="fas fa-lock text-success me-1"></i>Strict CAD / Drawing Confidentiality Guaranteed
                </span>
                <span>ISO 9001:2015 Standards</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactRFQ;
